import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/lib/instrumented-toast";
import { queryKeys } from "@/lib/query-keys";
import { useApiInstance } from "@/providers/api-provider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import React from "react";

function extractErrorDetail(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const resp = (error as { response?: { data?: { detail?: string } } }).response;
    if (resp?.data?.detail) return resp.data.detail;
  }
  if (error instanceof Error) return error.message;
  return "Unknown error";
}

interface ServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
}

export interface ServiceAccount {
  id: string;
  organization_id: string;
  organization_name: string;
  jfrog_username: string;
  is_active: boolean;
  created_by_user_id: string;
  created_by_user_email: string;
  created_at: string;
  jfrog_status: "valid" | "invalid" | "unknown";
}

export function ServiceAccountDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
}: ServiceAccountDialogProps) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const saQuery = useQuery({
    queryKey: queryKeys.jfrogServiceAccount(organizationId),
    queryFn: async (): Promise<ServiceAccount | null> => {
      try {
        const response = await useApiInstance.get(`/api/v1/organizations/${organizationId}/jfrog-service-account`);
        return response.data;
      } catch (error: unknown) {
        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404 || axiosError.response?.status === 403) return null;
        }
        throw error;
      }
    },
    enabled: open,
  });

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await useApiInstance.post(`/api/v1/organizations/${organizationId}/jfrog-service-account`);
      toast.success(`Service account created for "${organizationName}"`);
      queryClient.invalidateQueries({ queryKey: queryKeys.jfrogServiceAccount(organizationId) });
    } catch (error) {
      const detail = extractErrorDetail(error);
      toast.error(`Failed to create service account: ${detail}. You can safely retry.`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await useApiInstance.delete(`/api/v1/organizations/${organizationId}/jfrog-service-account`);
      toast.success(`Service account deleted for "${organizationName}"`);
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.jfrogServiceAccount(organizationId) });
    } catch (error) {
      toast.error(`Failed to delete service account: ${extractErrorDetail(error)}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isCreating && !isDeleting) {
      setConfirmDelete(false);
      onOpenChange(false);
    }
  };

  const sa = saQuery.data;
  const isLoading = saQuery.isLoading;
  const hasSA = saQuery.isSuccess && sa !== null && sa !== undefined;
  const hasError = saQuery.isError;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>JFrog Service Account</DialogTitle>
          <DialogDescription>{organizationName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading service account...</span>
            </div>
          ) : hasError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Unable to load service account</AlertTitle>
              <AlertDescription>
                <p>
                  Could not retrieve the service account status for this organization. This may be a temporary network
                  or server issue.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => saQuery.refetch()}
                  disabled={saQuery.isRefetching}
                >
                  {saQuery.isRefetching ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-3 w-3" />
                      Retry
                    </>
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          ) : hasSA ? (
            <>
              <div className="flex items-center gap-2">
                {sa.jfrog_status === "valid" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : sa.jfrog_status === "invalid" ? (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
                <span className="text-sm font-medium">
                  {sa.jfrog_status === "valid"
                    ? "Active service account"
                    : sa.jfrog_status === "invalid"
                      ? "Invalid service account"
                      : "Service account (unverified)"}
                </span>
                {sa.jfrog_status === "valid" ? (
                  <Badge variant="default">Valid</Badge>
                ) : sa.jfrog_status === "invalid" ? (
                  <Badge variant="destructive">Invalid</Badge>
                ) : (
                  <Badge variant="secondary">Unknown</Badge>
                )}
              </div>

              {sa.jfrog_status === "invalid" && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Token invalid</AlertTitle>
                  <AlertDescription>
                    The JFrog token for this service account has been revoked or deleted outside of the Customer Portal.
                    Delete this service account and create a new one.
                  </AlertDescription>
                </Alert>
              )}

              {sa.jfrog_status === "unknown" && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Unable to verify</AlertTitle>
                  <AlertDescription>
                    Could not reach JFrog to verify this service account's token. This may be a temporary network issue.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2 rounded-md border p-3">
                <div className="grid grid-cols-[120px_1fr] gap-1 text-sm">
                  <span className="text-muted-foreground">Username</span>
                  <span className="font-mono">{sa.jfrog_username}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1 text-sm">
                  <span className="text-muted-foreground">Created by</span>
                  <span>{sa.created_by_user_email}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1 text-sm">
                  <span className="text-muted-foreground">Created at</span>
                  <span>{new Date(sa.created_at).toLocaleString()}</span>
                </div>
              </div>

              {confirmDelete ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Confirm deletion</AlertTitle>
                  <AlertDescription>
                    This will revoke the JFrog token and delete the service account user. Workloads or users that rely
                    on this specific account may lose access to asset downloads or registry token creation.
                  </AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No service account</AlertTitle>
              <AlertDescription>
                This organization does not have a JFrog service account. Asset downloads and registry token creation are
                using global credentials. Create a dedicated service account for better isolation and auditability.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {isLoading ? null : hasError ? (
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          ) : hasSA ? (
            confirmDelete ? (
              <>
                <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Confirm Delete"
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </>
            )
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isCreating}>
                Close
              </Button>
              <Button onClick={handleCreate} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create service account
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useServiceAccountStatus(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.jfrogServiceAccount(orgId),
    queryFn: async (): Promise<ServiceAccount | null> => {
      try {
        const response = await useApiInstance.get(`/api/v1/organizations/${orgId}/jfrog-service-account`);
        return response.data;
      } catch (error: unknown) {
        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404 || axiosError.response?.status === 403) return null;
        }
        throw error;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
